/**
 * Invitation status page (`/me/invite`).
 *
 * One page, six shapes: activated, pending request, rejection cooldown, ready to
 * apply, approved with a mailed code, and the closed entry point. The shape is
 * derived from a single server response rather than from locally combined flags,
 * so the page cannot disagree with the server about whether applying is allowed.
 *
 * Redemption links arrive as `?code=<invite code>`. A visitor who is not signed
 * in goes through the sign-in flow first (the guard keeps this address as the
 * return target) and the code is redeemed automatically on arrival. The manual
 * entry form stays available beneath every unactivated shape, because a mail
 * client may have rewritten the link, and a failed automatic redemption shows
 * its error instead of failing silently.
 */

import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { InviteStatusResponse } from '@shared/types'

import { PageHeader } from '../../components/layout/page-header'
import { ErrorAlert, ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { ApprovedPanel } from '../../components/invite/approved-panel'
import { ApplyForm } from '../../components/invite/apply-form'
import { ClosedPanel, CooldownPanel, InvitedPanel, PendingPanel } from '../../components/invite/panels'
import { RedeemForm } from '../../components/invite/redeem-form'
import { useCurrentUser } from '../../hooks/use-auth'
import { useInviteStatus, useRedeemInvite, useWithdrawInvite } from '../../hooks/use-invite'
import { mutationErrorText } from '../../lib/mutation-error'

/** The six states the page can render. */
type InviteShape = 'invited' | 'pending' | 'approved' | 'cooldown' | 'apply' | 'closed'

/**
 * Picks the shape for a status.
 *
 * Order matters: an activated account is never shown a form, an approved request
 * with a usable code outranks the apply form (the code already exists), and a
 * running rejection cooldown outranks applying. An approved request whose code
 * has expired (or was revoked) has no available code left, so it falls through to
 * the apply form the server already allows instead of stranding the applicant on
 * a panel whose only action is a resend that cannot work.
 */
function resolveShape(status: InviteStatusResponse, now: number): InviteShape {
  if (status.canCreateEvent) {
    return 'invited'
  }
  if (status.application?.status === 'pending') {
    return 'pending'
  }
  if (status.application?.status === 'approved' && status.pendingCodeAvailable) {
    return 'approved'
  }
  if (status.cooldownEndsAt !== null && status.cooldownEndsAt > now) {
    return 'cooldown'
  }
  return status.canApply ? 'apply' : 'closed'
}

export function InvitePage() {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const status = useInviteStatus()
  const withdraw = useWithdrawInvite()
  const [searchParams, setSearchParams] = useSearchParams()

  const requestedCode = (searchParams.get('code') ?? '').trim()
  const { mutate: redeemCode, isPending: isRedeeming, error: redeemError } = useRedeemInvite()
  // The code from the address bar is spent at most once per page load.
  const attemptedCode = useRef<string | null>(null)

  useEffect(() => {
    if (!requestedCode || attemptedCode.current === requestedCode) {
      return
    }
    attemptedCode.current = requestedCode
    redeemCode(
      { code: requestedCode },
      {
        onSuccess: () => {
          // Drop the code from the address bar so a refresh cannot replay it.
          setSearchParams({}, { replace: true })
        },
      },
    )
  }, [redeemCode, requestedCode, setSearchParams])

  const autoRedeemError = mutationErrorText(redeemError, t)
  const withdrawError = mutationErrorText(withdraw.error, t)
  const now = Math.floor(Date.now() / 1000)

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('nav.me'), to: '/me' },
          { label: t('nav.invite') },
        ]}
        title={t('invite.title')}
        description={t('invite.body')}
      />

      {isRedeeming ? <LoadingBlock label={t('invite.redeem.autoPending')} /> : null}

      {autoRedeemError ? (
        <ErrorAlert message={autoRedeemError} />
      ) : null}

      {status.isPending ? <LoadingBlock label={t('common.loading')} /> : null}

      {status.isError ? (
        <ErrorBlock
          title={t('error.internal_error')}
          retryLabel={t('common.retry')}
          onRetry={() => void status.refetch()}
        />
      ) : null}

      {withdrawError ? (
        <ErrorAlert message={withdrawError} />
      ) : null}

      {status.data ? (
        <InviteStatusSection
          status={status.data}
          email={user?.email ?? ''}
          now={now}
          requestedCode={requestedCode}
          onWithdraw={() => withdraw.mutate()}
          isWithdrawing={withdraw.isPending}
        />
      ) : null}
    </div>
  )
}

/**
 * The shape panel plus the fallback the shape does not cover itself.
 *
 * Every unactivated shape without its own code entry gets the manual form, so a
 * rewritten mail link never dead-ends.
 */
function InviteStatusSection({
  status,
  email,
  now,
  requestedCode,
  onWithdraw,
  isWithdrawing,
}: {
  status: InviteStatusResponse
  email: string
  now: number
  /** Code from the address bar, offered for a manual retry when automatic redemption failed. */
  requestedCode: string
  onWithdraw: () => void
  isWithdrawing: boolean
}) {
  const shape = resolveShape(status, now)

  return (
    <>
      <InviteShapePanel
        shape={shape}
        status={status}
        email={email}
        onWithdraw={onWithdraw}
        isWithdrawing={isWithdrawing}
      />
      {shape !== 'invited' && shape !== 'approved' ? (
        <RedeemForm initialCode={requestedCode} />
      ) : null}
    </>
  )
}

/** Renders exactly one of the six shapes. */
function InviteShapePanel({
  shape,
  status,
  email,
  onWithdraw,
  isWithdrawing,
}: {
  shape: InviteShape
  status: InviteStatusResponse
  email: string
  onWithdraw: () => void
  isWithdrawing: boolean
}) {
  if (shape === 'invited') {
    return <InvitedPanel canCreateEvent={status.canCreateEvent} />
  }
  if (shape === 'pending' && status.application) {
    return (
      <PendingPanel
        application={status.application}
        onWithdraw={onWithdraw}
        isWithdrawing={isWithdrawing}
      />
    )
  }
  if (shape === 'approved') {
    return <ApprovedPanel status={status} email={email} />
  }
  if (shape === 'cooldown' && status.application && status.cooldownEndsAt !== null) {
    return <CooldownPanel application={status.application} cooldownEndsAt={status.cooldownEndsAt} />
  }
  if (shape === 'apply') {
    return <ApplyForm lastApplication={status.application} />
  }
  return <ClosedPanel />
}
